import React from "react";
import { marked } from "marked";
import xss from "xss";
import { ErrorBoundary } from "react-error-boundary";

import { StriimButton, StriimFeedbackBlock, StriimTypography } from "@striim/striim-ui";
import { Box } from "@mui/material";
import { AssistantMessageProps } from "@copilotkit/react-ui";
import App from "app";
import { AI_AGENT_NAME } from "../../ai-constants";

const TEXT_RETRY_PROMPT =
    "The previous response's text couldn't be rendered in the UI — the markdown/formatting failed " +
    "to display. Please try again with simpler formatting or plain text.";
const TOOL_RETRY_PROMPT =
    "The previous response's interactive component (tool UI) couldn't be rendered in the UI. " +
    "Please try again withaskUser.";

const sendRetry = (prompt: string) => () => App.vent.trigger("striimAI:user_message", prompt);

interface MessageErrorFallbackProps {
    label: string;
    onRetry: () => void;
}

const MessageErrorFallback: React.FC<MessageErrorFallbackProps> = ({ label, onRetry }) => (
    <Box mt={1} data-test-id="striim-ai-message-error">
        <StriimFeedbackBlock
            variant="critical"
            content={
                <Box>
                    <StriimTypography variant="body4" color="greyscale.800" data-test-id="striim-ai-message-error-text">
                        {label}
                    </StriimTypography>
                    <br />
                    <Box mt={2}>
                        <StriimButton variant="primary" onClick={onRetry}>
                            Try Again
                        </StriimButton>
                    </Box>
                </Box>
            }
        />
    </Box>
);

const MessageText: React.FC<{ content?: string }> = ({ content }) => {
    const htmlMessage = marked.parse(content ?? "", { async: false });
    const sanitizedMessage = xss(htmlMessage);

    if (!(sanitizedMessage?.length > 0)) {
        return null;
    }

    return (
        <StriimTypography
            variant="body4"
            color={"greyscale.700"}
            sx={{ lineHeight: "1.6 !important", margin: "8px" }}
            dangerouslySetInnerHTML={{ __html: sanitizedMessage }}
            data-test-id="response-message"
        ></StriimTypography>
    );
};

const MessageToolUI: React.FC<{ message: AssistantMessageProps["message"] }> = ({ message }) => {
    return <>{message?.generativeUI?.()}</>;
};

const CustomAssistantMessage: React.FC<AssistantMessageProps> = ({ message }) => (
    <>
        <ErrorBoundary
            fallbackRender={() => (
                <MessageErrorFallback
                    label={`${AI_AGENT_NAME} couldn't display this response.`}
                    onRetry={sendRetry(TEXT_RETRY_PROMPT)}
                />
            )}
        >
            <MessageText content={message?.content} />
        </ErrorBoundary>
        <ErrorBoundary
            fallbackRender={() => (
                <MessageErrorFallback
                    label={`${AI_AGENT_NAME} couldn't display this component.`}
                    onRetry={sendRetry(TOOL_RETRY_PROMPT)}
                />
            )}
        >
            <MessageToolUI message={message} />
        </ErrorBoundary>
    </>
);

export default CustomAssistantMessage;
